import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Calendar, Users, Image, ChevronRight, MessageCircle, Clock, ArrowRight, Heart } from "lucide-react";
import { format } from "date-fns";
import SocialShare from "../components/SocialShare";
import DonationSection from "../components/DonationSection";
import NewsletterSection from "../components/NewsletterSection";
import RamadanAudioSection from "../components/RamadanAudioSection";

// Professional Hero Image
const HERO_IMAGE = "https://images.unsplash.com/photo-1657598329547-60f9d36b751f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBzdW4lMjByYXlzfGVufDB8fHx8MTc3MTg2NjQ2OXww&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_companions-hub-2/artifacts/xm3vjt0f_TCF_logo.jpg";

const committees = [
  { 
    id: "mosque-dawah", 
    name: "Mosque and Da'wah", 
    description: "Spiritual guidance and Islamic education",
    icon: "🕌", 
    color: "from-emerald-500 to-teal-600",
    bgColor: "bg-emerald-50"
  },
  { 
    id: "project-maintenance", 
    name: "Project and Maintenance", 
    description: "Building and maintaining community facilities",
    icon: "🔧", 
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50"
  },
  { 
    id: "welfare-publicity", 
    name: "Welfare and Publicity", 
    description: "Community outreach and support programs",
    icon: "💚", 
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-50"
  },
  { 
    id: "finance-investment", 
    name: "Finance and Investment", 
    description: "Financial stewardship and growth",
    icon: "💰", 
    color: "from-amber-500 to-orange-600",
    bgColor: "bg-amber-50"
  },
  { 
    id: "security-ethic", 
    name: "Security and Ethics", 
    description: "Safety and ethical standards",
    icon: "🛡️", 
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50"
  },
];

const Home = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [postsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/posts?limit=6`),
        axios.get(`${API}/events?upcoming=true&limit=4`)
      ]);
      setPosts(postsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-stone-50">
      {/* Hero Section - Professional & Immersive */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Modern mosque with sun rays" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-900/80 to-primary-900/40"></div>
        </div>
        
        {/* Content */}
        <div className="container-custom relative z-10 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium mb-8 animate-fade-in-up">
              <Heart className="w-4 h-4" />
              Serving humanity since establishment
            </div>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight animate-fade-in-up stagger-1">
              The Companions
              <span className="block text-primary-200">Foundation</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-100 mb-4 font-medium animate-fade-in-up stagger-2">
              Service to Humanity
            </p>
            
            <p className="text-lg text-white/80 mb-10 leading-relaxed max-w-2xl animate-fade-in-up stagger-3">
              Dedicated to serving the Muslim community through Islamic education, 
              welfare programs, and community development initiatives. Together, 
              we build a stronger, more compassionate community.
            </p>
            
            <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-4">
              <Button 
                asChild 
                className="bg-white text-primary-900 hover:bg-primary-50 rounded-full px-8 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                data-testid="hero-donate-btn"
              >
                <a href="#donate">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </a>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
                data-testid="hero-learn-more"
              >
                <Link to="/about">
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-stone-50 to-transparent"></div>
      </section>

      {/* Committees Section - Bento Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Pillars</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Five Committees, One Mission
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Each committee plays a vital role in serving our community with dedication and excellence.
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {committees.map((c, index) => (
              <Link
                key={c.id}
                to={`/committee/${c.id}`}
                className={`group bento-card p-8 ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                data-testid={`committee-card-${c.id}`}
              >
                <div className={`w-16 h-16 rounded-2xl ${c.bgColor} flex items-center justify-center text-3xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {c.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-900 transition-colors">
                  {c.name}
                </h3>
                <p className="text-gray-600 mb-4">{c.description}</p>
                <div className="flex items-center text-primary-600 font-medium text-sm group-hover:gap-3 gap-1 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Ramadan Audio Section */}
      <RamadanAudioSection />

      {/* Latest Updates & Events */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Posts - Main Content */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-1">Stay Updated</p>
                  <h2 className="font-heading text-3xl font-bold text-gray-900 tracking-tight">Latest News</h2>
                </div>
                {user?.is_admin && (
                  <Button asChild className="btn-primary" data-testid="create-post-btn">
                    <Link to="/admin">Create Post</Link>
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="rounded-2xl overflow-hidden animate-pulse">
                      <div className="h-48 bg-gray-200"></div>
                      <CardContent className="p-6">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <Card className="p-12 text-center rounded-3xl border-dashed">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">No Posts Yet</h3>
                  <p className="text-gray-500">Check back soon for updates from The Companions Foundation.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {posts.slice(0, 4).map((post, index) => (
                    <Card 
                      key={post.post_id} 
                      className={`card-professional overflow-hidden ${index === 0 ? 'md:col-span-2' : ''}`}
                      data-testid={`post-${post.post_id}`}
                    >
                      {post.image_url && (
                        <div className={`overflow-hidden ${index === 0 ? 'h-64' : 'h-48'}`}>
                          <img 
                            src={post.image_url} 
                            alt={post.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                          />
                        </div>
                      )}
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            post.category === 'news' ? 'bg-blue-100 text-blue-700' :
                            post.category === 'announcement' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {post.category}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(post.created_at)}</span>
                        </div>
                        <Link to={`/post/${post.post_id}`}>
                          <h3 className={`font-heading font-semibold text-gray-900 mb-2 hover:text-primary-900 transition-colors ${index === 0 ? 'text-2xl' : 'text-lg'}`}>
                            {post.title}
                          </h3>
                        </Link>
                        <p className="text-gray-600 line-clamp-2 mb-4">{post.content}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <Users className="w-4 h-4 text-primary-900" />
                            </div>
                            <span>{post.author_name}</span>
                          </div>
                          <SocialShare 
                            url={`${window.location.origin}/post/${post.post_id}`}
                            title={post.title}
                            compact
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar - Events */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="mb-8">
                  <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-1">Mark Your Calendar</p>
                  <h2 className="font-heading text-3xl font-bold text-gray-900 tracking-tight">Upcoming Events</h2>
                </div>

                {events.length === 0 ? (
                  <Card className="p-8 text-center rounded-2xl">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No upcoming events</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {events.map((event) => (
                      <Card
                        key={event.event_id}
                        className="p-5 rounded-2xl hover:shadow-lg transition-all group cursor-pointer"
                        data-testid={`event-preview-${event.event_id}`}
                      >
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col items-center justify-center text-white">
                            <span className="text-lg font-bold leading-none">{format(new Date(event.date), "d")}</span>
                            <span className="text-xs uppercase">{format(new Date(event.date), "MMM")}</span>
                          </div>
                          <div className="flex-grow min-w-0">
                            <h4 className="font-semibold text-gray-900 mb-1 truncate group-hover:text-primary-900 transition-colors">
                              {event.title}
                            </h4>
                            <p className="text-sm text-gray-500 truncate">{event.location}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                    <Link 
                      to="/events" 
                      className="flex items-center justify-center gap-2 py-3 text-primary-600 font-semibold hover:text-primary-900 transition-colors"
                    >
                      View all events <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}

                {/* Quick Links Card */}
                <Card className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-primary-900 to-primary-800 text-white">
                  <h3 className="font-heading font-semibold text-lg mb-4">Quick Links</h3>
                  <div className="space-y-3">
                    <Link to="/media-gallery" className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
                      <Image className="w-5 h-5" />
                      <span className="font-medium">Media Gallery</span>
                    </Link>
                    <Link to="/contact" className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
                      <MessageCircle className="w-5 h-5" />
                      <span className="font-medium">Contact Us</span>
                    </Link>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <div id="donate">
        <DonationSection />
      </div>

      {/* Newsletter Section */}
      <NewsletterSection />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900 text-white py-24 relative overflow-hidden">
        {/* Decorative pattern */}
        <div className="pattern-overlay absolute inset-0"></div>
        
        <div className="container-custom text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            Join Our Community
          </h2>
          <p className="text-primary-200 max-w-2xl mx-auto mb-10 text-lg">
            Be part of our mission to serve humanity. Register to stay updated with our activities 
            and contribute to building a stronger community.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              asChild 
              className="bg-white text-primary-900 hover:bg-primary-50 rounded-full px-10 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
              data-testid="cta-register"
            >
              <Link to="/register">Register Now</Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg font-medium"
              data-testid="cta-contact"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
